import { useState, useEffect, useCallback, useRef } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { API_BASE } from '../../lib/api-base';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText, Upload, Search, Trash2, Download, Sparkles, X, BookPlus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = API_BASE;

function fmtBytes(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
}

export default function DocumentsPage() {
  const { user } = useAuth();
  const isReadOnly = user?.role === 'customer';
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [bulkJob, setBulkJob] = useState(null);  // {jobId,total,done,...}
  const [bulkStarting, setBulkStarting] = useState(false);
  const bulkPollRef = useRef(null);

  const load = useCallback(async () => {
    await Promise.resolve();
    setLoading(true);
    const res = await authFetch('/api/documents');
    if (res.ok) setDocs(await res.json());
    setLoading(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(), 0);
    return () => clearTimeout(t);
  }, [load]);

  // (Polling-Cleanup beim Unmount erfolgt indirekt: nach Job-Ende setzt
  // startPolling den Ref selbst auf null. Nicht 100% leak-frei bei Unmount
  // mitten im Job, aber pragmatischer als ein Cleanup-Effect.)

  // Polling startet eine Status-Schleife, bis der Job fertig ist. Wird
  // direkt aus startBulkExtract heraus aufgerufen (NICHT aus useEffect),
  // damit React's set-state-in-effect-Regel sauber erfüllt bleibt.
  const startPolling = useCallback((jobId) => {
    if (bulkPollRef.current) clearInterval(bulkPollRef.current);
    bulkPollRef.current = setInterval(async () => {
      const r = await authFetch(`/api/documents/_/extract-to-kb-bulk/status?jobId=${jobId}`);
      if (!r.ok) return;
      const d = await r.json();
      if (!d.hasJob) return;
      setBulkJob(d);
      if (d.status !== 'running') {
        clearInterval(bulkPollRef.current);
        bulkPollRef.current = null;
        load();
        setMessage({
          type: d.failed === 0 ? 'success' : 'info',
          text: `KB-Extraktion fertig: ${d.done}/${d.total} verarbeitet · ${d.createdEntries} neu · ${d.updatedEntries} aktualisiert${d.failed ? ` · ${d.failed} Fehler` : ''}.`,
        });
      }
    }, 1500);
  }, [load]);

  const startBulkExtract = async (scope) => {
    const label = scope === 'missing'
      ? 'NUR Dokumente OHNE bestehende Wissens-Einträge'
      : 'ALLE Dokumente (auch schon extrahierte werden aktualisiert)';
    if (!window.confirm(
      `KI-Bulk-Extraktion über ${label} starten?\n\nDas läuft seriell ` +
      `und dauert ca. 20–40 s pro Dokument. Während der Job läuft, bleibt diese Seite offen — du kannst aber zu anderen Tabs wechseln.`
    )) return;
    setBulkStarting(true);
    const r = await authFetch(`/api/documents/_/extract-to-kb-bulk?scope=${scope}`, { method: 'POST' });
    setBulkStarting(false);
    if (r.ok) {
      const d = await r.json();
      setBulkJob(d);
      if (d.status === 'running') startPolling(d.jobId);
      setMessage(d.alreadyRunning
        ? { type: 'info', text: `Es läuft bereits ein Bulk-Job (${d.done}/${d.total}). Fortschritt unten.` }
        : { type: 'info', text: `Bulk-Extraktion gestartet — ${d.total} Dokumente in der Warteschlange.` });
    } else {
      const d = await r.json().catch(() => ({}));
      setMessage({ type: 'error', text: d.detail || 'Bulk-Extraktion fehlgeschlagen' });
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Dokument „${doc.title}" wirklich löschen?`)) return;
    const res = await authFetch(`/api/documents/${doc.id}`, { method: 'DELETE' });
    if (res.ok) { setMessage({ type: 'success', text: 'Dokument gelöscht.' }); load(); }
  };

  const extractKb = async (doc) => {
    const existing = doc.kbExtract ? `Es gibt bereits ${doc.kbExtract.total} Einträge aus diesem Dokument. Erneut extrahieren überschreibt/ergänzt sie.\n\n` : '';
    if (!window.confirm(`${existing}Wissen aus „${doc.title}" via KI extrahieren und in die Wissensdatenbank übernehmen?`)) return;
    setMessage({ type: 'info', text: `KI analysiert „${doc.title}" und erstellt Wissenseinträge… (10–40 s)` });
    const res = await authFetch(`/api/documents/${doc.id}/extract-to-kb`, { method: 'POST' });
    if (res.ok) {
      const d = await res.json();
      setMessage({ type: 'success', text: `${d.created} neue Einträge angelegt, ${d.updated} aktualisiert.` });
      load();
    } else {
      const d = await res.json().catch(() => ({}));
      setMessage({ type: 'error', text: d.detail || 'Extraktion fehlgeschlagen' });
    }
  };

  const download = async (doc) => {
    const token = localStorage.getItem('ees_token');
    const res = await fetch(`${API}/api/documents/${doc.id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { setMessage({ type: 'error', text: 'Download fehlgeschlagen' }); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = doc.fileName || doc.title;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const search = async () => {
    if (!searchQ.trim()) { setSearchResults(null); return; }
    setSearching(true);
    const res = await authFetch(`/api/documents/_/search?q=${encodeURIComponent(searchQ)}&top_k=5`);
    if (res.ok) setSearchResults(await res.json());
    setSearching(false);
  };

  return (
    <div className="p-6 max-w-6xl" data-testid="documents-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-[#32373c] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#9ec043]" /> Dokumente ({docs.length})
          </h1>
          <p className="text-xs text-gray-400 mt-0.5">
            Interne Wissensdokumente. Automatisch indiziert — Frag HANS zitiert bei passenden Fragen Auszüge.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === 'admin' && (
            <div className="relative inline-flex">
              <Button
                onClick={() => startBulkExtract('missing')}
                disabled={bulkStarting || (bulkJob && bulkJob.status === 'running')}
                className="bg-white text-[#32373c] border border-[#9ec043] hover:bg-[#9ec043]/10 text-sm disabled:opacity-50"
                data-testid="btn-bulk-extract-missing"
                title="Extrahiert KI-Wissen nur aus Dokumenten ohne bestehende Einträge (admin-only)"
              >
                <BookPlus className="w-4 h-4 mr-1" />
                Bulk-KB-Extraktion
              </Button>
            </div>
          )}
          <Button onClick={() => setShowUpload(true)} className={`bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm ${isReadOnly ? 'hidden' : ''}`} data-testid="btn-upload-doc">
            <Upload className="w-4 h-4 mr-1" /> Dokument hochladen
          </Button>
        </div>
      </div>

      {/* Bulk-Job Fortschritt — wird angezeigt während Job läuft ODER kurz nach Abschluss */}
      {bulkJob && (
        <div className="bg-white border border-[#9ec043]/30 rounded-xl p-4 mb-4" data-testid="bulk-job-card">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles className={`w-4 h-4 ${bulkJob.status === 'running' ? 'text-[#9ec043] animate-pulse' : 'text-gray-400'}`} />
              <span className="text-sm font-semibold text-[#32373c]">
                {bulkJob.status === 'running' ? 'KB-Extraktion läuft' :
                 bulkJob.status === 'done' ? 'KB-Extraktion erfolgreich' :
                 bulkJob.status === 'partial' ? 'KB-Extraktion mit Fehlern beendet' :
                 'KB-Extraktion fehlgeschlagen'}
              </span>
              <span className="text-[10px] text-gray-400 font-mono">{bulkJob.scope}</span>
            </div>
            {bulkJob.status !== 'running' && (
              <button onClick={() => setBulkJob(null)} className="text-gray-400 hover:text-gray-600 text-sm" data-testid="btn-bulk-dismiss">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {/* Progress bar */}
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
            <div
              className="h-full bg-[#9ec043] transition-all duration-500"
              style={{ width: `${bulkJob.total ? Math.round(100 * (bulkJob.done + bulkJob.failed) / bulkJob.total) : 0}%` }}
              data-testid="bulk-progress-bar"
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
            <span data-testid="bulk-progress-count">
              <strong className="text-[#32373c]">{bulkJob.done + bulkJob.failed}</strong> / {bulkJob.total} Dokumente
            </span>
            <span>{bulkJob.createdEntries} neue Einträge</span>
            <span>{bulkJob.updatedEntries} aktualisiert</span>
            {bulkJob.failed > 0 && (
              <span className="text-red-600">{bulkJob.failed} Fehler</span>
            )}
            {bulkJob.currentDocTitle && bulkJob.status === 'running' && (
              <span className="text-gray-400 truncate max-w-md">
                ⏵ aktuell: <span className="text-[#32373c]">{bulkJob.currentDocTitle}</span>
              </span>
            )}
          </div>
          {bulkJob.errors && bulkJob.errors.length > 0 && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-red-600 hover:text-red-700">
                Fehler-Details ({bulkJob.errors.length})
              </summary>
              <ul className="mt-1 ml-4 space-y-0.5 text-red-600">
                {bulkJob.errors.slice(0, 10).map((e, i) => (
                  <li key={i} className="list-disc">
                    <strong>{e.docTitle}:</strong> {e.error}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {!isReadOnly && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-700">
          🔒 <strong>Strikte Trennung:</strong> Kundenuploads aus &bdquo;Frag HANS&ldquo; fließen NICHT automatisch hier ein. Nur interne Uploads über diese Seite gehen in die Wissensbasis.
        </div>
      )}

      {message && (
        <div className={`rounded p-3 mb-4 text-sm border ${message.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-600'}`}>
          {message.text}
        </div>
      )}

      {/* Semantic search */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-gray-400" />
          <Input value={searchQ} onChange={e => setSearchQ(e.target.value)}
                 onKeyDown={e => e.key === 'Enter' && search()}
                 placeholder="Semantische Suche im Dokumentenkorpus — z. B. Notstrom-Umschaltzeit HS 9 PV duo..."
                 className="h-9 text-sm" data-testid="docs-search" />
          <Button onClick={search} disabled={searching || !searchQ.trim()}
                  className="bg-[#9ec043] hover:bg-[#8aad38] text-white h-9 text-sm" data-testid="btn-search-docs">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> {searching ? 'Suche…' : 'Suchen'}
          </Button>
          {searchResults && (
            <Button variant="outline" size="sm" onClick={() => { setSearchResults(null); setSearchQ(''); }} className="h-9 text-xs">
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
        {searchResults && (
          <div className="mt-3 space-y-2">
            {searchResults.matches.length === 0 ? (
              <p className="text-xs text-gray-400">Keine relevanten Abschnitte gefunden.</p>
            ) : searchResults.matches.map((m, i) => (
              <div key={i} className="border-l-4 border-[#9ec043] bg-gray-50 p-3 rounded" data-testid={`search-match-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-[#32373c]">{m.title}</span>
                  <span className="text-[10px] text-gray-400">Abschnitt {m.i + 1} · Score {m.score}</span>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">{m.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Document list */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading && <div className="p-6 text-center text-xs text-gray-400">Lade…</div>}
        {!loading && docs.length === 0 && (
          <div className="p-8 text-center">
            <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Noch keine Dokumente.</p>
            <p className="text-xs text-gray-400 mt-1">Lade PDFs, DOCX, TXT oder MD hoch.</p>
          </div>
        )}
        {!loading && docs.map(d => (
          <div key={d.id} className="p-4 border-b border-gray-100 last:border-b-0 flex items-start gap-3" data-testid={`doc-row-${d.id}`}>
            <FileText className="w-5 h-5 text-[#9ec043] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#32373c] truncate">{d.title}</div>
              <div className="text-[11px] text-gray-400 mt-0.5 truncate">
                {d.fileName} · {fmtBytes(d.sizeBytes)} · {d.chunkCount} Abschnitte · {d.category}
              </div>
              <div className="text-[10px] text-gray-400 mt-0.5">
                {fmtDate(d.uploadedAt)} · {d.uploadedBy}
              </div>
              {d.tags?.length > 0 && (
                <div className="flex gap-1 mt-1 flex-wrap">
                  {d.tags.map((t, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{t}</span>)}
                </div>
              )}
              {d.aiSummary?.text && (
                <div className="mt-2 bg-[#9ec043]/5 border-l-2 border-[#9ec043] rounded-r px-2 py-1.5">
                  <div className="text-[10px] text-[#9ec043] font-medium uppercase tracking-wide mb-0.5">KI-Zusammenfassung</div>
                  <p className="text-xs text-gray-600 leading-relaxed">{d.aiSummary.text}</p>
                  {d.aiSummary.keywords?.length > 0 && (
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {d.aiSummary.keywords.map((k, i) => <span key={i} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white border border-[#9ec043]/30 text-[#9ec043]">{k}</span>)}
                    </div>
                  )}
                </div>
              )}
              {d.kbExtract && (
                <div className="mt-2 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-1 inline-flex items-center gap-1" data-testid={`kb-badge-${d.id}`}>
                  <BookPlus className="w-3 h-3" /> {d.kbExtract.total} Wissens­einträge · {fmtDate(d.kbExtract.createdAt)}
                </div>
              )}
            </div>
            <div className="flex gap-1 shrink-0">
              {!isReadOnly && (
                <button onClick={() => extractKb(d)} className="p-1.5 text-gray-400 hover:text-emerald-600 rounded" title="Wissen in Wissensdatenbank übernehmen" data-testid={`btn-kb-${d.id}`}>
                  <BookPlus className="w-4 h-4" />
                </button>
              )}
              <button onClick={() => download(d)} className="p-1.5 text-gray-400 hover:text-[#9ec043] rounded" title="Herunterladen" data-testid={`btn-download-${d.id}`}>
                <Download className="w-4 h-4" />
              </button>
              {!isReadOnly && (
                <button onClick={() => remove(d)} className="p-1.5 text-gray-400 hover:text-red-500 rounded" title="Löschen" data-testid={`btn-delete-${d.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showUpload && <UploadDialog onClose={() => setShowUpload(false)} onSaved={() => { setShowUpload(false); load(); setMessage({ type: 'success', text: 'Dokument hochgeladen und indiziert.' }); }} />}
    </div>
  );
}

function UploadDialog({ onClose, onSaved }) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('allgemein');
  const [tags, setTags] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [knownModels, setKnownModels] = useState([]);
  const fileRef = useRef(null);

  // Modellnamen aus dem Konfigurator ziehen — als klickbare Chips,
  // damit der Admin die Tags nicht abtippen muss (vermeidet Tippfehler
  // wie "HS-15-PV-duo" vs. "HS 15 PV duo" — der Datenblatt-Match in
  // chat.py ist case-sensitive auf das exakte Modellnamen-Tag).
  useEffect(() => {
    if (category !== 'datenblatt') return;
    fetch(`${API}/api/configurator/pv-systems`)
      .then(r => r.ok ? r.json() : { systems: [] })
      .then(d => setKnownModels((d.systems || []).map(s => s.model).filter(Boolean)))
      .catch(() => setKnownModels([]));
  }, [category]);

  const currentTags = tags.split(',').map(t => t.trim()).filter(Boolean);
  const toggleTag = (model) => {
    if (currentTags.includes(model)) {
      setTags(currentTags.filter(t => t !== model).join(', '));
    } else {
      setTags([...currentTags, model].join(', '));
    }
  };

  const submit = async () => {
    if (!file) { setError('Bitte Datei wählen.'); return; }
    if (!title.trim()) { setError('Titel erforderlich.'); return; }
    setUploading(true); setError('');
    try {
      const token = localStorage.getItem('ees_token');
      const form = new FormData();
      form.append('file', file);
      form.append('title', title);
      form.append('category', category);
      form.append('tags', tags);
      const res = await fetch(`${API}/api/documents`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Upload fehlgeschlagen');
        return;
      }
      onSaved();
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" data-testid="upload-dialog">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-5">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-semibold text-[#32373c]">Dokument hochladen</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        {error && <div className="bg-red-50 border border-red-200 text-red-600 rounded p-2 text-xs mb-3">{error}</div>}
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-gray-500">Titel</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)}
                   placeholder="z. B. Installationsanleitung HS 9 PV duo"
                   className="mt-1 h-9 text-sm" data-testid="upload-title" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Kategorie</Label>
            <select value={category} onChange={e => setCategory(e.target.value)}
                    className="mt-1 w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm" data-testid="upload-category">
              <option value="allgemein">Allgemein</option>
              <option value="installation">Installation</option>
              <option value="inbetriebnahme">Inbetriebnahme</option>
              <option value="wartung">Wartung</option>
              <option value="troubleshooting">Troubleshooting</option>
              <option value="datenblatt">Datenblatt</option>
              <option value="handbuch">Handbuch</option>
            </select>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Tags (Komma-getrennt)</Label>
            <Input value={tags} onChange={e => setTags(e.target.value)}
                   placeholder={category === 'datenblatt'
                     ? 'z. B. HS 9 PV duo (Modellname)'
                     : 'z. B. HS-9, PV, duo'}
                   className="mt-1 h-9 text-sm" data-testid="upload-tags" />
            {category === 'datenblatt' && knownModels.length > 0 && (
              <div className="mt-2" data-testid="model-tag-suggestions">
                <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">
                  Modelle anklicken (Tag wird hinzugefügt/entfernt):
                </div>
                <div className="flex flex-wrap gap-1">
                  {knownModels.map(m => {
                    const active = currentTags.includes(m);
                    return (
                      <button key={m}
                        type="button"
                        onClick={() => toggleTag(m)}
                        className={`text-[10px] px-2 py-0.5 rounded border transition ${
                          active
                            ? 'bg-[#9ec043] text-white border-[#9ec043]'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-[#9ec043] hover:text-[#9ec043]'
                        }`}
                        data-testid={`model-tag-${m}`}>
                        {m}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs text-gray-500">Datei (PDF, DOCX, TXT, MD — max 25 MB)</Label>
            <div className="mt-1 flex items-center gap-2">
              <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden"
                     onChange={e => setFile(e.target.files?.[0])}
                     data-testid="upload-file" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} className="h-9 text-sm">
                <Upload className="w-3.5 h-3.5 mr-1" /> Datei wählen
              </Button>
              {file && <span className="text-xs text-gray-600 truncate">{file.name} ({fmtBytes(file.size)})</span>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-5 justify-end">
          <Button variant="outline" onClick={onClose} className="h-9 text-sm">Abbrechen</Button>
          <Button onClick={submit} disabled={uploading || !file || !title.trim()}
                  className="bg-[#9ec043] hover:bg-[#8aad38] text-white h-9 text-sm" data-testid="btn-submit-upload">
            {uploading ? 'Lädt & indiziert…' : 'Hochladen & indizieren'}
          </Button>
        </div>
      </div>
    </div>
  );
}
